import dayjs from 'dayjs'
import numeral from 'numeral'
import React, { useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { CrowdFund, Gift, OneOff } from 'src/components/atoms/Icons'
import { BulkGiftAlternative } from 'src/components/atoms/Icons/BulkGiftAlternative'
import { InternalLink } from 'src/components/atoms/Link'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Heading3 } from 'src/components/atoms/Typography'
import {
  DashboardActionPanel,
  DashboardActionPanelProps
} from 'src/components/molecules/DashboardActionPanel'
import { HistoryGraph } from 'src/components/molecules/HistoryGraph'
import { ImpactSummary } from 'src/components/molecules/ImpactSummary'
import { ZeroState } from 'src/components/molecules/ZeroState'

import {
  useGetDashboardDataQuery,
  useGetDashboardProjectsSupportedCountQuery
} from 'src/config/generated/graphql'
import {
  giftPath,
  donatePath,
  bulkGiftPath,
  listFundingPath,
  listRegistriesPath
} from 'src/config/paths'
import { AccountType } from 'src/types/account'

import { buildFunderGraphData, getAllTimeTreesFunded } from '../../helper'

const funderActions: DashboardActionPanelProps['actions'] = [
  {
    title: 'Fund native trees',
    href: donatePath(),
    imgSrc: <OneOff className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  },
  {
    title: 'Gift tree-cards',
    href: giftPath(),
    imgSrc: <Gift className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  },
  {
    title: 'Gift tree-cards in bulk (to 10+ people)',
    href: bulkGiftPath(),
    imgSrc: (
      <BulkGiftAlternative className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
    )
  },
  {
    title: 'Start or support a tree registry',
    href: listRegistriesPath,
    imgSrc: <CrowdFund className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  }
]

const businessActions: DashboardActionPanelProps['actions'] = [
  {
    title: 'Fund planting projects',
    href: donatePath(),
    imgSrc: <OneOff className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  },
  {
    title: 'Gift trees to your customers or staff',
    href: giftPath(),
    imgSrc: <Gift className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  },
  {
    title: 'Start or support a tree registry',
    href: listRegistriesPath,
    imgSrc: <CrowdFund className='w-[30px] sm:w-10 h-[30px] sm:h-10' />
  }
]

const ImpactDashboardPanel = () => {
  const { user } = useUser()

  const { data: projectsSupportedData } =
    useGetDashboardProjectsSupportedCountQuery({
      variables: {
        funderId: user?.Account?.FunderId
      },
      skip: !user?.Account?.FunderId
    })
  const { data: dashboardData, loading: dashboardDataLoading } =
    useGetDashboardDataQuery()

  const allTimeTreesFunded = useMemo(
    () => getAllTimeTreesFunded(dashboardData),
    [dashboardData]
  )

  const hasFunded = useMemo(() => allTimeTreesFunded > 0, [allTimeTreesFunded])

  return (
    <LoadingSpinner isLoading={dashboardDataLoading}>
      <div className='flex flex-col gap-5 sm:gap-10'>
        {hasFunded ? (
          <ImpactSummary
            treesFunded={allTimeTreesFunded}
            projectsSupported={
              projectsSupportedData?.SupportedProjectsView_aggregate.aggregate
                ?.count || 0
            }
            accountJoinYear={dayjs(user?.Account?.CreatedOn).year().toString()}
          />
        ) : null}
        <div
          className={`flex gap-5 sm:gap-10 ${
            hasFunded ? 'flex-col lg:flex-row' : 'flex-col'
          }`}
        >
          <div className='bg-white flex-auto rounded-[10px] p-5 sm:p-10'>
            {hasFunded ? (
              <div className='h-full flex flex-col gap-5 sm:gap-10'>
                <div className='flex flex-col gap-[10px]'>
                  <Heading3 className='!m-0 !text-[18px] !leading-[26px] sm:!text-[28px] sm:!leading-[39px] !font-semibold'>
                    Funding history
                  </Heading3>
                  <span className='text-[14px] sm:text-base font-medium'>
                    Trees funded by year.{' '}
                    <InternalLink href={listFundingPath}>
                      View all transactions
                    </InternalLink>
                  </span>
                </div>
                <div className='h-72 md:h-80 relative'>
                  <HistoryGraph
                    valueFormatter={v => numeral(v).format('0,0')}
                    data={buildFunderGraphData(dashboardData)}
                  />
                </div>
              </div>
            ) : (
              <ZeroState
                title='Make a positive impact for nature'
                description='Your impact summary will display here once you start funding.'
                image='/img/zero-states/funder.png'
              />
            )}
          </div>
          <DashboardActionPanel
            title={hasFunded ? 'Boost your impact' : 'Start making an impact'}
            actions={
              user?.Account?.Type === AccountType.Business
                ? businessActions
                : funderActions
            }
          />
        </div>
      </div>
    </LoadingSpinner>
  )
}

export default ImpactDashboardPanel
